import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FLASH_1,
  FLASH_2,
  NEWS_1,
  NEWS_2,
  FLASH_1_EXPIRY,
  FLASH_2_EXPIRY,
} from "./consts";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "@/components/Toast";
import {
  useAddNewsletterMutation,
  useDeleteNewsletterMutation,
  useGetCommunities,
  useGetCommunityNewsSettings,
  useUpdateAnnouncementMutation,
} from "@/services";
import {
  getRequiredFields,
  removeEmptyFields,
  ROLE_BOM,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { approvalSchema } from "./validations";
import { RootState } from "@/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { setCommunityData } from "@/redux/slices";

const useAnnouncements = () => {
  const dispatch = useDispatch();
  const {communityData, masterDataPermission} = useSelector((state: RootState) => state.currentData);
  const newsletter1Ref = useRef(null);
  const newsletter2Ref = useRef(null);
  const [flashIndex, setFlashIndex] = useState<number>();
  const [expiredDate1, setExpiredDate1] = useState<any>(null);
  const [expiredDate2, setExpiredDate2] = useState<any>(null);
  const [date, setDate] = useState(moment());
  const [open, setOpen] = useState<boolean>(false);
  const [key, setKey] = useState<number>(Math.random() * 2);
  const id = communityData?.communityId;

  const form = useForm<any>({
    defaultValues: {
      [FLASH_1]: undefined,
      [FLASH_2]: undefined,
      [NEWS_1]: undefined,
      [NEWS_2]: undefined,
    },
    resolver: zodResolver(approvalSchema),
  });
  // Dynamically determine required fields
  const requiredFields = getRequiredFields(approvalSchema);

  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  // const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);
  
  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});
  
  const {
    data: communityNewsSettings,
    isLoading: isNewsSettingsLoading,
    isRefetching: isNewsSettingsRefetching,
    isError: isNewSettingError
  } = useGetCommunityNewsSettings({
    community_id: id || "",
    queryParams: {
      enabled: !!(id),
    },
  });
  
  useEffect(() => {
    if (communityList?.data) {
      setKey(Math.random() * 2);
    }
  }, [communityList]);
  
  useEffect(() => {
    if (isNewSettingError) {
      form.reset({
        [FLASH_1]: "",
        [FLASH_2]: "",
        [NEWS_1]: undefined,
        [NEWS_2]: undefined,
      });
      setExpiredDate1(null);
      setExpiredDate2(null);
    }
  }, [isNewSettingError]);

  useEffect(() => {
    if (communityNewsSettings) {
      form.reset({
        [FLASH_1]: communityNewsSettings?.data?.[FLASH_1] || undefined,
        [FLASH_2]: communityNewsSettings?.data?.[FLASH_2] || undefined,
        [NEWS_1]: {
          name: communityNewsSettings?.data?.newsletter1FileName || undefined,
        },
        [NEWS_2]: {
          name: communityNewsSettings?.data?.newsletter2FileName || undefined,
        },
      });
      setExpiredDate1(
        communityNewsSettings?.data?.[FLASH_1_EXPIRY] || moment().add(1, "week")
      );
      setExpiredDate2(
        communityNewsSettings?.data?.[FLASH_2_EXPIRY] || moment().add(1, "week")
      );
    }
  }, [communityNewsSettings]);

  const { mutateAsync: updateInfo, isPending: isUpdatePending } =
    useUpdateAnnouncementMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {},
    });

  const { mutateAsync: updateNewsletter, isPending: isUpdateLetterPending } =
    useAddNewsletterMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {},
    });

  const { mutateAsync: deleteNewsletter, isPending: isDeleteLetterPending } =
    useDeleteNewsletterMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {},
    });

  const onSubmit = async (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    const newData = {
      ..._data,
      [FLASH_1_EXPIRY]: expiredDate1,
      [FLASH_2_EXPIRY]: expiredDate2,
    };

    try {
      // 1. Update Info
      await updateInfo({
        id: id || "",
        data: newData,
      });

      // 2. Newsletter 1: delete or update
      if (communityNewsSettings?.data?.newsletter1FileName && !_data[NEWS_1]) {
        await deleteNewsletter({
          id: id || "",
          news_number: 1,
        });
      } else if (_data[NEWS_1] instanceof File) {
        await updateNewsletter({
          id: id || "",
          data: { file: _data[NEWS_1] },
          news_number: 1,
        });
      }

      // 3. Newsletter 2: delete or update
      if (communityNewsSettings?.data?.newsletter2FileName && !_data[NEWS_2]) {
        await deleteNewsletter({
          id: id || "",
          news_number: 2,
        });
      } else if (_data[NEWS_2] instanceof File) {
        await updateNewsletter({
          id: id || "",
          data: { file: _data[NEWS_2] },
          news_number: 2,
        });
      }

      // Done
      toast.success("Announcements Updated Successfully");

    } catch (error) {
      toast.error("Something went wrong while updating.");
    }
  };

  const onChangeClick = (index: number) => (e: React.SyntheticEvent) => {
    e.preventDefault();
    setOpen(true);
    if (index == 1) {
      setDate(expiredDate1);
    } else {
      setDate(expiredDate2);
    }
    setFlashIndex(index);
  };

  const onSaveDate = () => {
    if (flashIndex == 1) {
      setExpiredDate1(date);
    } else {
      setExpiredDate2(date);
    }
    setOpen(false);
  };

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };
  
  return {
    form,
    onSubmit,
    requiredFields,
    newsletter1Ref,
    newsletter2Ref,
    expiredDate1,
    setExpiredDate1,
    expiredDate2,
    setExpiredDate2,
    onChangeClick,
    onSaveDate,
    setDate,
    date,
    open,
    setOpen,
    isUpdatePending,
    id,
    isNewsSettingsLoading,
    isNewsSettingsRefetching,
    isUpdateLetterPending,
    isDeleteLetterPending,
    communityList,
    communityId: id,
    onCommunityChange,
    isCommunityLoading,
    key,
    hasPermission: hasAddPermission || hasUpdatePermission,
  };
};

export default useAnnouncements;
